import React from 'react';
import { connect } from 'react-redux';
import { Segment, Dimmer, Loader, Dropdown, Image } from 'semantic-ui-react';
import Currency from 'react-currency-formatter';
import PropTypes from 'prop-types';
import { uiActions, projectActions } from '../../actions/index';
import Strings from '../../common/Strings';
import './ProjectCreateOrEdit.css';
import { getCurrencyText } from '../../helpers/currency';
import Events from '../../analytics/Events';
import Analytics from '../../analytics/Analytics';

const tinycolor = require('tinycolor2');

class ProjectCreateOrEdit extends React.Component {
  constructor(props) {
    super(props);

    if (props.project) {
      const { project } = props;
      this.state = {
        id: project.id,
        createdAt: project.createdAt,
        name: project.name,
        color: project.color,
        notes: project.info,
        hourlyRate: project.hourlyRate,
        currencyCode: project.currencyCode,
        amountFocused: false,
        nameErrorText: '',
        showHoverColor: false,
        hoverColor: project.color,
      };
    } else {
      this.state = {
        id: '',
        createdAt: '',
        name: '',
        color: '#4A90E2',
        notes: '',
        hourlyRate: 0.0,
        currencyCode: this.props.currencyCode,
        amountFocused: false,
        nameErrorText: '',
        showHoverColor: false,
        hoverColor: '#4A90E2',
      };
    }
  }

  componentWillReceiveProps(newProps) {
    if (this.props.project && !newProps.project) {
      this.setState({
        id: '',
        createdAt: '',
        name: '',
        color: '#4A90E2',
        notes: '',
        hourlyRate: 0.0,
        currencyCode: this.props.currencyCode,
        amountFocused: false,
        nameErrorText: '',
        showHoverColor: false,
        hoverColor: '#4A90E2',
      });
    }
  }

  onNameChange = e => {
    const name = e.target.value;
    this.setState({ name, nameErrorText: '' });
  };

  onNotesChange = e => {
    this.setState({ notes: e.target.value });
  };

  onAmountChange = e => {
    this.setState({ hourlyRate: e.target.value });
  };

  onAmountFocusIn = () => {
    this.setState({ amountFocused: true });
    document.getElementById('project-create-amount-input').focus();
  };

  onAmountFocusOut = () => {
    this.setState({ amountFocused: false });
  };

  onAmountKeyPress = e => {
    if (e.key === 'Enter') {
      this.onAmountFocusOut();
    }
  };

  onMouserOverColorPicker = () => {
    this.setState({ showHoverColor: true });
  };

  onMouserOutColorPicker = () => {
    this.setState({ showHoverColor: false });
  };

  onMouseMoveColorPicker = e => {
    const h = (e.nativeEvent.offsetX / 347) * 360;
    let s = 100 - (e.nativeEvent.offsetY / 225) * 100;
    const l = 50;

    if (s > 100) {
      s = 100;
    }

    const color = tinycolor({ h, s, l });
    const hex = color.toHexString();
    this.setState({ hoverColor: hex });
  };

  onMouseUpColorPicker = () => {
    this.setState({ color: this.state.hoverColor });
  };

  getTitleText() {
    if (this.props.project) {
      return Strings.PROJECT_EDIT.editProject;
    }
    return Strings.PROJECT_CREATE.addProject;
  }

  getSaveButtonText() {
    if (this.props.project) {
      return Strings.PROJECT_EDIT.saveChanges;
    }
    return Strings.PROJECT_CREATE.createProject;
  }

  validateName() {
    const name = this.state.name.trim().toLowerCase();
    if (name.length === 0) {
      this.setState({ nameErrorText: Strings.ERROR.projectNameEmpty });
      return false;
    }

    const { project, projects } = this.props;
    let found = false;
    if (project && project.name.toLowerCase().trim() === name) {
      found = true;
    }

    if (!found) {
      const existingProject = projects.find(
        p => p.name.toLowerCase().trim() === name,
      );
      if (existingProject) {
        this.setState({ nameErrorText: Strings.ERROR.projectNameExists });
        return false;
      }
    }

    return true;
  }

  changeCurrency = (e, { value }) => {
    this.setState({ currencyCode: value });
  };

  close = () => {
    this.props.dispatch(uiActions.addProjectClicked(false));
    this.props.dispatch(uiActions.editProjectClicked(undefined));
  };

  createProject = () => {
    if (this.validateName()) {
      const {
        id,
        createdAt,
        name,
        color,
        notes,
        hourlyRate,
        currencyCode,
      } = this.state;
      let hourlyRateFloat = hourlyRate;
      if (typeof hourlyRateFloat === 'string') {
        hourlyRateFloat = parseFloat(hourlyRateFloat.replace(',', '.'));
      }
      const project = {
        id,
        createdAt,
        name,
        color,
        info: notes,
        hourlyRate: hourlyRateFloat,
        currencyCode,
      };

      if (this.props.project) {
        Analytics.logEvent(Events.PROJECT_UPDATE, {
          name: project.name,
          notes: project.notes,
          hourlyRate: project.hourlyRate,
          currencyCode: project.currencyCode,
        });

        this.props.dispatch(projectActions.updateProject(project, this.close));
      } else {
        this.props.dispatch(projectActions.createProject(project));
      }
    }
  };

  renderProjectNameError() {
    if (this.state.nameErrorText.length > 0) {
      return <p id="project-create-name-error">{this.state.nameErrorText}</p>;
    }
    return <div />;
  }

  render() {
    const {
      name,
      color,
      notes,
      hourlyRate,
      currencyCode,
      amountFocused,
      nameErrorText,
      hoverColor,
    } = this.state;
    const inputDividerColor = nameErrorText.length > 0 ? '#EB5757' : '#BDBDBD';
    const colorPickerColor = this.state.showHoverColor ? hoverColor : color;
    return (
      <div className="project-create-root">
        <div className="project-create-content-container">
          <Segment basic className="project-create-content">
            <Dimmer active={this.props.loading} inverted>
              <Loader inverted />
            </Dimmer>
            <div className="project-create-title-container">
              <h1
                id="project-create-title-text"
                className="project-details-title"
              >
                {this.getTitleText()}
              </h1>
            </div>
            <div className="project-create-name-root">
              <div id="project-create-color-circle-container">
                <Image
                  id="project-create-color-circle-image"
                  src="/ellipse-with-shadow.svg"
                />
                <div
                  id="project-create-color-circle"
                  style={{ backgroundColor: color }}
                />
              </div>
              <div className="project-create-name-container">
                <input
                  id="project-create-name-input"
                  placeholder={Strings.PROJECT_CREATE.projectName}
                  value={name}
                  onChange={this.onNameChange}
                />
                <div
                  id="project-create-name-input-divider"
                  style={{ backgroundColor: inputDividerColor }}
                />
              </div>
            </div>
            {this.renderProjectNameError()}
            <div
              id="project-create-color-picker"
              style={{ backgroundColor: colorPickerColor }}
              onMouseOver={this.onMouserOverColorPicker}
              onMouseOut={this.onMouserOutColorPicker}
              onMouseMove={this.onMouseMoveColorPicker}
              onMouseUpCapture={this.onMouseUpColorPicker}
            >
              <p
                id="project-create-color-picker-hint"
                hidden={this.state.showHoverColor}
              >
                {Strings.PROJECT_CREATE.colorPickerHint}
              </p>
            </div>
            <p id="project-create-notes-header">
              {Strings.PROJECT_CREATE.notes}
            </p>
            <div id="project-create-notes-container">
              <textarea
                id="project-create-notes-input"
                onChange={this.onNotesChange}
                value={notes}
              />
            </div>
            <div id="project-create-amount-root">
              <div className="project-create-amount-container">
                <p className="project-create-amount-header">
                  {Strings.PROJECT_CREATE.hourlyRate}
                </p>
                <input
                  id="project-create-amount-input"
                  type="number"
                  value={hourlyRate}
                  onChange={this.onAmountChange}
                  onFocus={this.onAmountFocusIn}
                  onBlur={this.onAmountFocusOut}
                  onKeyPress={this.onAmountKeyPress}
                  style={{ opacity: amountFocused ? 1 : 0 }}
                />
                <div
                  onClick={this.onAmountFocusIn}
                  style={{ opacity: amountFocused ? 0 : 1 }}
                >
                  <p id="project-create-amount-value">
                    <Currency quantity={hourlyRate} currency={currencyCode} />
                  </p>
                </div>
                <div id="project-create-amount-divider" />
              </div>
              <div className="project-create-amount-container">
                <p className="project-create-amount-header">
                  {Strings.PROJECT_CREATE.currency}
                </p>
                <Dropdown
                  text={getCurrencyText(this.state.currencyCode)}
                  basic
                  id="project-create-drop-down"
                >
                  <Dropdown.Menu>
                    <Dropdown.Item
                      value={Strings.CURRENCIES.dollarCode}
                      onClick={this.changeCurrency}
                      className="project-create-drop-down-item"
                    >
                      {Strings.CURRENCIES.dollar}
                    </Dropdown.Item>
                    <Dropdown.Item
                      value={Strings.CURRENCIES.euroCode}
                      onClick={this.changeCurrency}
                      className="project-create-drop-down-item"
                    >
                      {Strings.CURRENCIES.euro}
                    </Dropdown.Item>
                    <Dropdown.Item
                      value={Strings.CURRENCIES.yenCode}
                      onClick={this.changeCurrency}
                      className="project-create-drop-down-item"
                    >
                      {Strings.CURRENCIES.yen}
                    </Dropdown.Item>
                    <Dropdown.Item
                      value={Strings.CURRENCIES.poundCode}
                      onClick={this.changeCurrency}
                      className="project-create-drop-down-item"
                    >
                      {Strings.CURRENCIES.pound}
                    </Dropdown.Item>
                    <Dropdown.Item
                      value={Strings.CURRENCIES.rubleCode}
                      onClick={this.changeCurrency}
                      className="project-create-drop-down-item"
                    >
                      {Strings.CURRENCIES.ruble}
                    </Dropdown.Item>
                    <Dropdown.Item
                      value={Strings.CURRENCIES.rupeeCode}
                      onClick={this.changeCurrency}
                      className="project-create-drop-down-item"
                    >
                      {Strings.CURRENCIES.rupee}
                    </Dropdown.Item>
                    <Dropdown.Item
                      value={Strings.CURRENCIES.rialCode}
                      onClick={this.changeCurrency}
                      className="project-create-drop-down-item"
                    >
                      {Strings.CURRENCIES.rial}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                <div id="project-create-currency-divider" />
              </div>
            </div>
            <button
              id="project-create-button"
              className="common-green-button"
              onClick={this.createProject}
            >
              {this.getSaveButtonText()}
            </button>
          </Segment>
        </div>
        <Image
          id="project-create-close-button"
          src="/icon/close.svg"
          onClick={this.close}
        />
      </div>
    );
  }
}

ProjectCreateOrEdit.propTypes = {
  dispatch: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  project: PropTypes.shape({
    id: PropTypes.string.isRequired,
    ownerUid: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    info: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    archivedAt: PropTypes.string,
    hourlyRate: PropTypes.number.isRequired,
    currencyCode: PropTypes.string.isRequired,
  }),
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  currencyCode: PropTypes.string.isRequired,
};

ProjectCreateOrEdit.defaultProps = {
  project: undefined,
};

const mapStateToProps = state => ({
  loading: state.uiState.isProjectUpdating,
  project: state.uiState.editProject,
  projects: state.projects,
  currencyCode: state.user.settings.defaultCurrency,
});

export default connect(mapStateToProps)(ProjectCreateOrEdit);



// WEBPACK FOOTER //
// ./src/components/projects/ProjectCreateOrEdit.js